import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { ToolFormConfig } from "../formConfigs/baseFormConfig.js";

export interface BaseToolFormData<TSettings> {
  name: string;
  description: string;
  settings: TSettings;
}

interface UseToolFormParams<TSettings, TFormSettings> {
  config: ToolFormConfig<TSettings, TFormSettings>;
  initialData: BaseToolFormData<TSettings> | undefined;
  onSave: (data: BaseToolFormData<TSettings>) => Promise<void>;
}

export interface UseToolFormResult {
  isEditMode: boolean;
  isFormValid: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  renderForm: () => ReactNode;
}

export function useBaseToolForm<TSettings, TFormSettings>(
  params: UseToolFormParams<TSettings, TFormSettings>,
): UseToolFormResult {
  const { config, initialData, onSave } = params;

  // Initial form values: original tool values from props
  const initialName = initialData?.name ?? "";
  const initialDescription = initialData?.description ?? "";
  const initialSettings = useMemo(
    () => config.initialize(initialData?.settings),
    [config, initialData?.settings],
  );

  // Changed form values: changes the user made compared to the original
  const [changedName, setChangedName] = useState<string | undefined>(undefined);
  const [changedDescription, setChangedDescription] = useState<
    string | undefined
  >(undefined);
  const [changedSettings, setChangedSettings] = useState<
    Partial<TFormSettings>
  >({});

  const discardChanges = useCallback(() => {
    setChangedName(undefined);
    setChangedDescription(undefined);
    setChangedSettings({});
  }, []);

  // Current form values: computed from initial + changed
  const name = changedName ?? initialName;
  const description = changedDescription ?? initialDescription;
  const settings = useMemo(
    () => ({
      ...initialSettings,
      ...changedSettings,
    }),
    [initialSettings, changedSettings],
  );

  // Sanitized form values: this is what we'll save.
  const sanitizedName = name.trim();
  const sanitizedDescription = description.trim();

  const isEditMode = Boolean(initialData);

  // Perform real-time validation that disables the save button
  const isFormValid =
    sanitizedName !== "" &&
    sanitizedDescription !== "" &&
    config.validate(settings);

  // Detect changes (only relevant in edit mode)
  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return false;
    }

    return (
      changedName !== undefined ||
      changedDescription !== undefined ||
      Object.keys(changedSettings).length !== 0
    );
  }, [isEditMode, changedName, changedDescription, changedSettings]);

  const handleSettingsChange = useCallback(
    (changedSettings: Partial<TFormSettings>) => {
      setChangedSettings((prev) => {
        const merged = { ...prev, ...changedSettings };
        return isEqual(prev, merged) ? prev : merged;
      });
    },
    [],
  );

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useEffectEvent(async () => {
    if (!isFormValid) {
      return;
    }

    // Perform just-in-time validation before saving, which may update the form with errors
    const result = config.save(settings);

    if (!result.isValid) {
      setChangedSettings((prev) => ({
        ...prev,
        ...result.formSettings,
      }));
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        name: sanitizedName,
        description: sanitizedDescription,
        settings: result.settings,
      });

      // Clear changed state after successful save
      discardChanges();
    } finally {
      setIsSaving(false);
    }
  });

  const handleDiscard = useEffectEvent(() => {
    discardChanges();
  });

  const renderForm = () => {
    const Form = config.Form;

    return (
      <Form
        isEditMode={isEditMode}
        name={name}
        description={description}
        settings={settings}
        onNameChange={setChangedName}
        onDescriptionChange={setChangedDescription}
        onSettingsChange={handleSettingsChange}
      />
    );
  };

  return {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave: handleSave,
    onDiscard: handleDiscard,
    renderForm,
  };
}
